const MANIFEST_URL = `${process.env.PUBLIC_URL || ""}/data/romi-digital-landings.json`;
export const DIGITAL_SECTION_KEYS = ["HERO", "PROBLEMA_OPORTUNIDAD", "SOLUCION_TRANSFORMACION", "OFERTA_CONFIANZA", "CTA_FINAL"];
const CTA_KEYS = new Set(["HERO", "OFERTA_CONFIANZA", "CTA_FINAL"]);
const IMAGE_SOURCES = new Set(["uploaded", "generated", "legacy_url"]);

const isRecord = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);
const text = (value, max = 500) => typeof value === "string" ? value.trim().slice(0, max) : "";
const textList = (value, maxItems, maxLength) => Array.isArray(value) ? value.map((item) => text(item, maxLength)).filter(Boolean).slice(0, maxItems) : [];
const safeUrl = (value, { hotlink = false } = {}) => {
  const url = text(value, 2048);
  if (!url || url.includes("\\") || url.includes("..") || /^file:/i.test(url) || /^[a-z]:/i.test(url)) return "";
  if (!hotlink && url.startsWith("/romi-commerce-ai/uploads/digital-landings/") && !url.startsWith("//")) return url;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && parsed.hostname ? parsed.href : "";
  } catch {
    return "";
  }
};

export function normalizeDigitalCampaignLanding(value, expectedSlug = "") {
  if (!isRecord(value)) return null;
  const slug = text(value.slug, 100).toLowerCase();
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) || (expectedSlug && slug !== expectedSlug)) return null;
  const productoId = Number(value.productoId);
  const productoNombre = text(value.productoNombre, 190);
  const tipoDigital = text(value.tipoDigital, 120) || "producto digital";
  const visualSystem = text(value.visualSystem, 1).toLowerCase();
  if (!Number.isInteger(productoId) || productoId < 1 || !productoNombre || !/^[e-k]$/.test(visualSystem)) return null;
  if (!Array.isArray(value.offers) || value.offers.length < 1 || value.offers.length > 3) return null;
  const offerRefs = new Set();
  const offers = value.offers.map((raw) => {
    if (!isRecord(raw)) return null;
    const ref = text(raw.ref, 40);
    const hotlink = safeUrl(raw.hotlink, { hotlink: true });
    const price = raw.price === null ? null : Number(raw.price);
    const currency = text(raw.currency, 12).toUpperCase();
    if (!/^offer-[1-3]$/.test(ref) || offerRefs.has(ref) || !hotlink || !currency || (price !== null && (!Number.isFinite(price) || price < 0 || price > 100000))) return null;
    offerRefs.add(ref);
    return { ref, hotlink, price, currency };
  });
  if (offers.some((offer) => !offer)) return null;
  if (!Array.isArray(value.sections) || value.sections.length !== DIGITAL_SECTION_KEYS.length) return null;
  const sections = value.sections.map((raw, index) => {
    if (!isRecord(raw) || raw.key !== DIGITAL_SECTION_KEYS[index]) return null;
    const image = safeUrl(raw.image);
    const imageSource = text(raw.imageSource, 20);
    if (!image || !IMAGE_SOURCES.has(imageSource)) return null;
    let cta = null;
    if (raw.cta !== null && raw.cta !== undefined) {
      if (!isRecord(raw.cta)) return null;
      const label = text(raw.cta.label, 70);
      const offerRef = text(raw.cta.offerRef, 40);
      if (!label || !offerRefs.has(offerRef) || !CTA_KEYS.has(raw.key)) return null;
      cta = { label, offerRef };
    }
    return {
      key: raw.key,
      headline: text(raw.headline, 300),
      subtitle: text(raw.subtitle, 500),
      body: textList(raw.body, 2, 1000),
      chips: textList(raw.chips, 4, 140),
      image,
      imageSource,
      cta,
    };
  });
  if (sections.some((section) => !section)) return null;
  const recovery = isRecord(value.recovery) ? value.recovery : {};
  const metadata = isRecord(value.metadata) ? value.metadata : {};
  return {
    slug,
    productoId,
    productoNombre,
    tipoDigital,
    visualSystem,
    imageContainsCopy: value.imageContainsCopy === true,
    sections,
    offers,
    recovery: { facts: textList(recovery.facts, 5, 300), expiresAfterMs: 2 * 60 * 60 * 1000 },
    metadata: { title: text(metadata.title, 190) || productoNombre, description: text(metadata.description, 300) },
  };
}

export async function loadDigitalCampaignLanding(slug, { signal } = {}) {
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(String(slug || ""))) return null;
  try {
    const response = await fetch(MANIFEST_URL, { cache: "no-store", signal });
    if (!response.ok) return null;
    const manifest = await response.json();
    if (!isRecord(manifest) || manifest.version !== 1 || manifest.source !== "romi-commerce-ai" || !Array.isArray(manifest.landings)) return null;
    const candidate = manifest.landings.find((landing) => landing?.slug === slug);
    return normalizeDigitalCampaignLanding(candidate, slug);
  } catch (error) {
    if (error?.name !== "AbortError") console.warn("No se pudo cargar la landing digital pública.");
    return null;
  }
}
